import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { userService } from '../../services/userService';
import { useApi } from '../../hooks/useApi';
import styles from './Profile.module.css';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';

const Profile = () => {
    const userId = localStorage.getItem('userId');
    const { loading, executeRequest } = useApi();
    const [profilePreview, setProfilePreview] = useState(null);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [canEditRestricted, setCanEditRestricted] = useState(true);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors, isDirty }
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            username: '',
            password: '',
            confirmPassword: ''
        }
    });

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await executeRequest(userService.getUserById, userId);
                const userData = response.data || response;
                reset({
                    name: userData.name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    username: userData.username || '',
                });
                if (userData.userimage) {
                    let profilePic = userData.userimage;
                    if (!profilePic.startsWith('data:image')) {
                        profilePic = `data:image/jpeg;base64,${profilePic}`;
                    }
                    setProfilePreview(profilePic);
                }
                // Check if user is authorized to edit restricted fields
                // Authorized username is 'Anirudh123'
                setCanEditRestricted(userData.username === 'Anirudh123');
            } catch (error) {
                toast.error('Failed to load profile data');
            }
        };

        if (userId) {
            fetchUserData();
        }
    }, [userId, executeRequest, reset]);

    const handleImageChange = (e) => {
        if (!canEditRestricted) {
            toast.warning('You do not have permission to change the profile image');
            return;
        }
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const onProfileSubmit = async (data) => {
        try {
            const updateData = {
                name: data.name,
                username: data.username,
                email: data.email,
                phone: data.phone,
                image: profilePreview // base64 string
            };

            // backend endpoint: router.put("/users/:id", updateUserProfile);
            // We might need a direct api call if userService doesn't have this specific method
            // or we can add it to userService.
            await executeRequest(userService.updateUserProfile, userId, updateData);
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        }
    };

    const onPasswordSubmit = async (data) => {
        if (data.password !== data.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        try {
            await executeRequest(userService.changePassword, userId, {
                password: data.password,
                confirmPassword: data.confirmPassword
            });
            toast.success('Password changed successfully');
            setIsChangingPassword(false);
            setValue('password', '');
            setValue('confirmPassword', '');
        } catch (error) {
            toast.error('Failed to change password');
        }
    };

    return (
        <div className={styles.profileContainer}>
            <div className={styles.header}>
                <h1 className={styles.title}>My Profile</h1>
                <p className={styles.subtitle}>Manage your account information and security settings</p>
            </div>

            <div className={styles.content}>
                <div className={styles.grid}>
                    {/* Profile Details Selection */}
                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>Personal Information</h2>
                        <form onSubmit={handleSubmit(onProfileSubmit)} className={styles.form}>
                            <div className={styles.avatarSection}>
                                <div className={styles.avatarWrapper}>
                                    {profilePreview ? (
                                        <img src={profilePreview} alt="Profile" className={styles.avatarImage} />
                                    ) : (
                                        <div className={styles.avatarPlaceholder}>
                                            {watch('name')?.charAt(0) || 'U'}
                                        </div>
                                    )}
                                    {canEditRestricted && (
                                        <label htmlFor="avatar-upload" className={styles.uploadOverlay}>
                                            <span>Change</span>
                                            <input
                                                id="avatar-upload"
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className={styles.hiddenInput}
                                            />
                                        </label>
                                    )}
                                </div>
                                {canEditRestricted && <p className={styles.avatarHint}>Click to upload a new profile picture</p>}
                            </div>

                            <div className={styles.formGrid}>
                                <Input
                                    label="Full Name"
                                    {...register('name', { required: 'Name is required' })}
                                    error={errors.name?.message}
                                />
                                <Input
                                    label="Username"
                                    {...register('username')}
                                    error={errors.username?.message}
                                />
                                <Input
                                    label="Email Address"
                                    type="email"
                                    {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "Invalid email address"
                                        }
                                    })}
                                    error={errors.email?.message}
                                />
                                <Input
                                    label="Phone Number"
                                    {...register('phone', { required: 'Phone is required' })}
                                    error={errors.phone?.message}
                                    disabled={!canEditRestricted}
                                />
                            </div>

                            <div className={styles.formActions}>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    loading={loading}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </form>
                    </div>

                    {/* Password Section */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h2 className={styles.cardTitle}>Account Security</h2>
                            {canEditRestricted ? (
                                <Button
                                    variant="outline"
                                    size="small"
                                    onClick={() => setIsChangingPassword(!isChangingPassword)}
                                >
                                    {isChangingPassword ? 'Cancel' : 'Change Password'}
                                </Button>
                            ) : (
                                <span className={styles.restrictedLabel}>Restricted</span>
                            )}
                        </div>

                        {isChangingPassword ? (
                            <form onSubmit={handleSubmit(onPasswordSubmit)} className={styles.form}>
                                <div className={styles.formGrid}>
                                    <Input
                                        label="New Password"
                                        type="password"
                                        {...register('password', { required: 'Password is required', minLength: 6 })}
                                        error={errors.password?.message}
                                    />
                                    <Input
                                        label="Confirm New Password"
                                        type="password"
                                        {...register('confirmPassword', { required: 'Please confirm your password' })}
                                        error={errors.confirmPassword?.message}
                                    />
                                </div>
                                <div className={styles.formActions}>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        loading={loading}
                                    >
                                        Update Password
                                    </Button>
                                </div>
                            </form>
                        ) : (
                            <div className={styles.securityInfo}>
                                <p>Ensure your account is using a long, random password to stay secure.</p>
                                <div className={styles.securityBadge}>
                                    <span className={styles.securityIcon}>🔒</span>
                                    <span>Two-factor authentication is not yet enabled.</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
